import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, ButtonGroup, Card, Col, Form, InputGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { setHours, setMinutes, setSeconds, subDays } from 'date-fns';
import { NumericFormat } from 'react-number-format';

import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import DatePicker from '@components/Common/AwpDatePicker';
import { WeatherStationDropdown } from '@components/Common/WeatherStationDropdown';
import DatasetsDropdown from '@components/dataTables/PageFilters/Datasets';

import {
  DATA_RANGE,
  INI_END_DATE,
  INI_START_DATE,
  DEFAULT_PRESET_ID_FOR_ALL_DATASETS,
  EnabledFeature,
  FORM_MODE,
  STATE_STATUS,
} from '@awp/constants';
import { debounce } from '@awp/helpers/Utils';
import { getPresetsForDropdown, getDeferOnlyPresetsForDropdown } from '@stores/graphPresetSlice';
import { setAllFilteredWeatherStations, setDataRange, setEndDateEpoch } from '@stores/weatherStationSlice';
import { selectSelectedDatasetOptions, setSelectedDatasetOptions } from '@awp/stores/dataTablePageSlice';
import { IconFunnelFilter } from '@components/awpIcons';
import { useMultiStationContext } from '@components/Graphs/MultiStation/MultiStationContext';
import {
  selectPreset,
  setPreset,
  selectDataRange,
  selectEndDate,
  selectStartDate,
  setEndDate as setEndDateSlice,
  setStartDate,
  derivedObservatioIds,
} from '@stores/dataVisualizationSlice';
import MultiStationButtonGroup from '@graphs/MultiStation/MultiStationButtonGroup';
import { AwpPresetContainer } from '@components/Common/AwpPresetControls';
import UserPresetModal from '@components/dataTables/UserPresetModal';
import {
  setUserPreset,
  setDeleteUserPresetStatus,
  deleteUserPreset,
  selectAllUserPresets,
} from '@stores/userPresetSlice';

/**
 * A component for configuring data filters including preset selection, weather station selection, and date range selection.
 * @returns {JSX.Element} The data filter form.
 */
const DataFilters = () => {
  const { dataRange, setEndDate, hasDeferWeatherStation, status, isForGraph } = useMultiStationContext();
  const dispatch = useDispatch();

  const endDate = useSelector(selectEndDate);
  const startDate = useSelector(selectStartDate);
  const dataRangeSelector = useSelector(selectDataRange);
  const dispatchDatasetOptions = (options) => dispatch(setSelectedDatasetOptions(options));
  const selectedDatasetOptions = useSelector(selectSelectedDatasetOptions);
  const selectedDerivedObservatioIds = useSelector(derivedObservatioIds);
  const filteredSelectedDatasets =
    selectedDerivedObservatioIds != null &&
    selectedDerivedObservatioIds.length > 0 &&
    selectedDatasetOptions != null &&
    selectedDatasetOptions.length > 0 != null
      ? selectedDatasetOptions.filter((x) => !selectedDerivedObservatioIds.includes(x.typeId))
      : selectedDatasetOptions;
  const userAllPresets = useSelector(selectAllUserPresets);
  const [presetModal, setPresetModal] = useState(false);
  const [mode, setMode] = useState('');

  useEffect(() => {
    if (
      startDate.value === new Date(new Date(INI_START_DATE)).getTime() &&
      endDate.value === new Date(new Date(INI_END_DATE)).getTime()
    ) {
      dispatch(
        setStartDate({
          value: setSeconds(setMinutes(setHours(subDays(new Date(), dataRangeSelector), 0), 0), 0).getTime(),
          error: '',
          disabled: false,
        }),
      );
      dispatch(
        setEndDateSlice({
          value: setSeconds(setMinutes(setHours(new Date(), 23), 59), 0).getTime(),
          error: '',
          disabled: false,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (!dataRange) return;
    dispatch(setEndDateEpoch(dataRange.endDate));
    dispatch(setDataRange(dataRange.displayRange));
    dispatch(setAllFilteredWeatherStations());
  }, [dataRange]);

  /**
   * Updates the end date in the data range when a new date is selected.
   * @param {string|Date} newEndDate - The new end date selected from the date picker.
   */
  const dispatchDateChanges = (newEndDate) => {
    let todayDate = new Date();
    const endDateEpoch =
      new Date(newEndDate).toDateString() == todayDate.toDateString()
        ? todayDate.getTime()
        : setMinutes(setHours(new Date(newEndDate), 23), 59).getTime();

    setEndDate(endDateEpoch);
  };

  // Debounce the dispatchDateChanges or any other logic in handleChange
  const handleChange = useCallback(
    debounce((val) => {
      dispatchDateChanges(val);
    }, 1000),
    [dispatchDateChanges],
  ); // Dependency array ensures stability if dispatchDateChanges changes

  const colMd = 5;

  const handlePresetModalClosed = () => {
    dispatch(setUserPreset(null));
    setPresetModal(false);
  };

  const getPresetById = (id) => {
    let p = null;
    if (userAllPresets && userAllPresets.length > 0) p = userAllPresets.find((x) => x.Id + '' === id + '');
    if (p) return { ...p };
    return null;
  };
  const presetEdit = (e) => {
    if (!e || !e.target || !e.target.id) return;
    e.preventDefault();
    const id = e.target.id;
    let p = getPresetById(id);
    if (!p) return;
    dispatch(setUserPreset(p));
    setMode(FORM_MODE.EDIT);
    setPresetModal(true);
  };

  const presetDelete = (e) => {
    if (!e || !e.target || !e.target.id) return;
    e.preventDefault();
    const id = e.target.id;
    presetDeleteById(id);
  };
  const presetDeleteById = (id) => {
    if (!id) return;
    if (id > 0) {
      let p = getPresetById(id);
      if (!p) return;
      setDeleteUserPresetStatus(STATE_STATUS.LOADING);
      dispatch(setUserPreset(p));
      dispatch(deleteUserPreset());
      dispatch(setUserPreset(null));
    }
  };

  return (
    <>
      <Accordion defaultActiveKey="0">
        <Card border="light" className={'page-card__fullscreen'}>
          <CardHeaderToggle
            eventKey="0"
            as="h3"
            title={isForGraph ? 'Multi Station Data Graphs' : 'Data Filter Selection'}
          />
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <div>
                <Form>
                  <Form.Row style={{ alignItems: 'center' }}>
                    <Col lg={2} md={colMd} sm={colMd} className="mb-3" style={{ zIndex: 30 }}>
                      <PresetDropdown isForGraph={isForGraph} zIndex={'1001'} />
                    </Col>
                    <Col lg={3} md={colMd} sm={colMd} className="mb-3 pt-1" style={{ zIndex: 20 }}>
                      {hasDeferWeatherStation || !isForGraph ? <WeatherStationDropdown zIndex={'1000'} /> : null}
                    </Col>
                    <Col lg={2} md={colMd} sm={colMd} className="mb-3">
                      <div>
                        <Form.Label>
                          <b>End Date</b>
                        </Form.Label>
                        <div style={{ width: '100%' }}>
                          <DatePicker
                            elementState={{
                              value: dataRange ? dataRange.endDate : new Date().getTime(),
                              error: '',
                              disabled: false,
                            }}
                            className="form-control input-sm"
                            dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                            handleChange={handleChange}
                            showTime={{ isEnabled: false }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col lg={2} md={colMd} sm={colMd} style={{ zIndex: 10 }} className="mb-3">
                      <AwpDisplayRange />
                    </Col>
                    {isForGraph && (
                      <Col lg={3} md={4} sm={4} className="mt-3">
                        <MultiStationButtonGroup />
                      </Col>
                    )}
                    {!isForGraph && (
                      <Col lg={3} md={3} sm={4} className="mb-3" style={{ zIndex: 16 }}>
                        <Form.Label>
                          <b>Datasets</b>
                        </Form.Label>
                        <div className="input-group">
                          <DatasetsDropdown
                            withImages={false}
                            setSelectedOptions={dispatchDatasetOptions}
                            selectedOptions={filteredSelectedDatasets}
                          />
                          {EnabledFeature.WeatherStationPresetButton && (
                            <div className="input-group-append">
                              <IconFunnelFilter
                                alt=" Preset Filter"
                                title=" Preset Filter"
                                style={{
                                  width: '2rem',
                                  height: '2rem',
                                  paddingTop: '.5rem',
                                  paddingLeft: '.5rem',
                                  cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  dispatch(setUserPreset(null));
                                  setMode(FORM_MODE.CREATE);
                                  setPresetModal(true);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </Col>
                    )}
                  </Form.Row>
                  <Form.Row>
                    {EnabledFeature.WeatherStationPresetButton &&
                      userAllPresets &&
                      userAllPresets.length > 0 &&
                      status.multiStationData === STATE_STATUS.SUCCESS && (
                        <AwpPresetContainer
                          clickPresetEdit={presetEdit}
                          clickPresetDelete={presetDelete}
                          items={userAllPresets}
                        />
                      )}
                  </Form.Row>
                </Form>
              </div>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <UserPresetModal
        visible={presetModal}
        mode={mode}
        setMode={setMode}
        deletePreset={presetDeleteById}
        closeModal={handlePresetModalClosed}
      />
    </>
  );
};

/**
 * Dropdown component for selecting a preset configuration.
 * @param {Object} props - Component properties including a postChange function and zIndex for styling.
 * @returns {JSX.Element} A Select dropdown populated with graph presets.
 */
const PresetDropdown = (props) => {
  const dispatch = useDispatch();
  const { dataRange, updateDataRange } = useMultiStationContext();
  const graphPresets = useSelector(props.isForGraph ? getPresetsForDropdown : getDeferOnlyPresetsForDropdown);
  const selectedOption = useSelector(selectPreset);
  //const [selectedOption, setSelectedOption] = useState(null);
  const setAllDasetsOption = () => {
    const defaultOption = graphPresets.find((opt) => opt.value === DEFAULT_PRESET_ID_FOR_ALL_DATASETS);
    if (defaultOption) {
      dispatch(setPreset(defaultOption));
      if (props.postChange && props.postChange instanceof Function) props.postChange(defaultOption);
    }
  };
  const setDefaultOption = () => {
    if (!selectedOption) {
      if (graphPresets?.length && !props.isForGraph) {
        setAllDasetsOption();
      }
    } else {
      //selected for non-graph
      if (graphPresets?.length && !props.isForGraph) {
        const isDefer = graphPresets.find((opt) => opt.value === selectedOption.value);
        //the current is not defer only(then selected in graph) -> then switch to DEFAULT_PRESET_ID_FOR_ALL_DATASETS
        if (!isDefer) {
          setAllDasetsOption();
        }
      } else if (graphPresets?.length && props.isForGraph) {
        // selected for graph
        if (selectedOption.value === DEFAULT_PRESET_ID_FOR_ALL_DATASETS) {
          dispatch(setPreset(null));
          if (props.postChange && props.postChange instanceof Function) props.postChange(null);
        }
      }
    }
  };
  useEffect(() => {
    setDefaultOption();
  }, [props.isForGraph]);

  /**
   * Custom format for options in the dropdown.
   * @param {Object} option - The current option being rendered.
   * @returns {JSX.Element} A formatted option with an icon and text.
   */
  const formatOptionLabel = (option) => {
    return (
      <div style={{ display: 'flex' }}>
        <div>
          <i
            className={`fas fa-solid ${option.isPublic ? 'fa-globe' : 'fa-user'} mx-1 my-1`}
            style={{ color: 'blue' }}
          />
          {` ${option.label}`}
        </div>
      </div>
    );
  };

  const selectStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: 'white',
      borderColor: '#adb5bd',
      color: '#494949',
    }),
    container: (styles) => ({ ...styles, width: '100%', zIndex: !props.zIndex ? '1000' : props.zIndex }),
    input: (styles) => ({ ...styles }),
    placeholder: (styles) => ({ ...styles, color: '#494949' }),
    dropdownIndicator: (styles) => ({ ...styles, color: '#797979', '&:hover': { color: '#494949' } }),
  };

  /**
   * Handles changes to the dropdown's selected item and invokes a callback if provided.
   * @param {Object} selOption - The selected option.
   */
  const handleChange = (selOption) => {
    const p = graphPresets.find((x) => x.value === selOption.value);
    dispatch(setPreset(p));
    if (props.postChange && props.postChange instanceof Function) props.postChange(selOption);

    const displayRangeOption = getDisplayRangeOption(dataRange.displayRange)[0];
    if (displayRangeOption.label === 'Custom') updateDataRange(dataRange.endDate, displayRangeOptions[0].value);
  };

  return (
    <>
      <Form.Label>
        <b>Presets</b>
      </Form.Label>
      <div className="input-group">
        <Select
          options={graphPresets}
          styles={selectStyles}
          value={selectedOption}
          placeholder={'Select Presets'}
          onChange={handleChange}
          closeMenuOnSelect={true}
          isDisabled={false}
          formatOptionLabel={formatOptionLabel}
        />
      </div>
    </>
  );
};

const displayRangeOptions = [
  { label: '4 Days', value: 4 },
  { label: '7 Days', value: 7 },
  { label: '14 Days', value: 14 },
  { label: `${DATA_RANGE.MAXIMUM} Days`, value: DATA_RANGE.MAXIMUM },
  { label: 'Custom', value: DATA_RANGE.CUSTOM }, //the select doesn't set the item when it's -1
];

const getDisplayRangeOption = (value) => {
  let option = displayRangeOptions.filter((o) => o.value === value);
  if (option.length === 0) {
    option = displayRangeOptions.filter((o) => o.value === DATA_RANGE.CUSTOM);
  }

  return option;
};

/**
 * Component for selecting the display range of the data.
 * @returns {JSX.Element} A component allowing users to select how much data to display.
 */
const AwpDisplayRange = () => {
  const { dataRange, updateDataRange, maxDays } = useMultiStationContext();
  const [isCustomDataRange, setIsCustomDataRange] = useState(false);
  const [selectedValue, setSelectedValue] = useState(getDisplayRangeOption(dataRange?.displayRange));

  useEffect(() => {
    if (!dataRange) return;
    const dataRangeOption = getDisplayRangeOption(dataRange.displayRange);
    setSelectedValue(dataRangeOption);
    setIsCustomDataRange(dataRangeOption[0].value === DATA_RANGE.CUSTOM);
  }, [dataRange]);

  /**
   * Dispatches changes to the display range state.
   * @param {number} newDataRange - The new display range value.
   */
  const dispatchDisplayRangeChanges = (newDataRange) => {
    if (!isNaN(newDataRange)) {
      updateDataRange(dataRange.endDate, newDataRange);
    }
  };

  /**
   * Handles changes to the display range selection.
   * @param {number} value - The selected display range value.
   */
  const dataRangeChange = (value) => {
    setSelectedValue(getDisplayRangeOption(value));

    if (value === DATA_RANGE.CUSTOM) {
      setIsCustomDataRange(true);
    } else {
      setIsCustomDataRange(false);
      dispatchDisplayRangeChanges(value);
    }
  };
  const getMaxDayLabel = () => {
    const _maxDays = maxDays ? maxDays : DATA_RANGE.MINIMUM;
    if (_maxDays > DATA_RANGE.MAXIMUM) return ` (max ${_maxDays} days)`;
    return '';
  };
  return (
    <>
      <Form.Label>
        <b>Display Range{getMaxDayLabel()}</b>
      </Form.Label>
      <InputGroup style={{ zIndex: '1000' }}>
        <Select
          className="data-range-select"
          options={displayRangeOptions}
          value={selectedValue}
          onChange={(e) => {
            dataRangeChange(e.value);
          }}
        />
        <ButtonGroup className="data-range-input">
          <NumericFormat
            isAllowed={(values) => {
              const { floatValue } = values;
              const _maxDays = maxDays ? maxDays : DATA_RANGE.MAXIMUM;
              return floatValue === undefined || (floatValue <= _maxDays && floatValue >= DATA_RANGE.MINIMUM);
            }}
            onKeyUp={(e) => {
              e.persist();
              setTimeout(() => {
                if (e.target != null) dispatchDisplayRangeChanges(parseInt(e.target.value));
              }, 1500);
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            allowNegative={false}
            decimalScale={0}
            disabled={!isCustomDataRange}
            valueIsNumericString={true}
            className="form-control input-sm data-range-custom"
            thousandSeparator={false}
            value={dataRange?.displayRange}
          />
        </ButtonGroup>
      </InputGroup>
    </>
  );
};

export default DataFilters;
